import { lazy, Suspense, type FC } from 'react';

import { lazily } from 'react-lazily';

import ReportGoods from '@/pages/reports/goods';
import { Spinner } from 'flowbite-react';
import { createBrowserRouter } from 'react-router-dom';
import ProtectedRoute from './protected-route';
const ReparsePage = lazy(() => import('@/pages/reparse'));
const SettingsTransferPage = lazy(() => import('@/pages/settings-transfer'));
const SentryTestPage = lazy(() => import('@/pages/sentry-test'));

const {
  ForgotPasswordPage,
  ProfileLockPage,
  ResetPasswordPage,
  SignInPage,
  SignUpPage,
  InvitationRegisterPage,
  ActivateAccount,
} = lazily(() => import('@/pages/authentication'));

const { DashboardPage } = lazily(() => import('@/pages/root/'));
const { DashboardSearch } = lazily(() => import('@/pages/dashboard-search/'));
const { EcommerceBillingPage, EcommerceInvoicePage, EcommerceProductsPage } =
  lazily(() => import('@/pages/e-commerce'));
const { KanbanPage } = lazily(() => import('@/pages/kanban'));
const { NotFoundPage, ServerErrorPage, MaintenancePage, PricingPage } = lazily(
  () => import('@/pages/common')
);
const { UserFeedPage, UserListPage, UserProfilePage, UserSettingsPage } =
  lazily(() => import('@/pages/users'));
const {
  MailingInboxPage,
  MailingReadPage,
  MailingReplyPage,
  MailingComposePage,
} = lazily(() => import('@/pages/mailing'));

const { MyTeams } = lazily(() => import('@/pages/my-teams'));

const RouterLoader = () => (
  <div className="absolute z-10 flex h-screen w-screen items-center justify-center">
    <Spinner size="xl" />
  </div>
);

const withProtectedRoute = (Component: FC, isFooter?: boolean) => {
  return (
    <Suspense fallback={<RouterLoader />}>
      <ProtectedRoute isFooter={isFooter}>
        <Component />
      </ProtectedRoute>
    </Suspense>
  );
};

const withSuspense = (Component: FC) => {
  return (
    <Suspense fallback={<RouterLoader />}>
      <Component />
    </Suspense>
  );
};

export default createBrowserRouter(
  [
    { path: '/', element: withProtectedRoute(DashboardPage), index: true },
    { path: '/reparse', element: withProtectedRoute(ReparsePage), index: true },
    {
      path: '/settings-transfer',
      element: withProtectedRoute(SettingsTransferPage),
    },
    { path: '/dashboard-search', element: withProtectedRoute(DashboardSearch) },
    { path: '/reports/goods', element: withProtectedRoute(ReportGoods) },
    { path: '/activate/:uid/:token', element: withSuspense(ActivateAccount) },
    {
      path: '/my-teams',
      element: withProtectedRoute(MyTeams),
    },
    {
      path: '/mailing/compose',
      element: withProtectedRoute(MailingComposePage, false),
    },
    {
      path: '/mailing/inbox',
      element: withProtectedRoute(MailingInboxPage, false),
    },
    {
      path: '/mailing/read',
      element: withProtectedRoute(MailingReadPage, false),
    },
    {
      path: '/mailing/reply',
      element: withProtectedRoute(MailingReplyPage, false),
    },
    { path: '/kanban', element: withProtectedRoute(KanbanPage, false) },
    { path: '/pages/pricing', element: withProtectedRoute(PricingPage) },
    {
      path: '/pages/maintenance',
      element: withProtectedRoute(MaintenancePage),
    },
    { path: '/pages/404', element: withProtectedRoute(NotFoundPage) },
    { path: '/pages/500', element: withProtectedRoute(ServerErrorPage) },
    { path: '/sentry-test', element: withSuspense(SentryTestPage) },
    { path: '/authentication/sign-in', element: withSuspense(SignInPage) },
    { path: '/authentication/sign-up', element: withSuspense(SignUpPage) },
    {
      path: '/invitations/:user_id/:token',
      element: withSuspense(InvitationRegisterPage),
    },
    {
      path: '/authentication/forgot-password',
      element: withSuspense(ForgotPasswordPage),
    },
    {
      path: '/reset-password/:userId/:token',
      element: withSuspense(ResetPasswordPage),
    },
    {
      path: '/authentication/profile-lock',
      element: withSuspense(ProfileLockPage),
    },
    {
      path: '/e-commerce/billing',
      element: withProtectedRoute(EcommerceBillingPage),
    },
    {
      path: '/e-commerce/invoice',
      element: withProtectedRoute(EcommerceInvoicePage),
    },
    {
      path: '/e-commerce/products',
      element: withProtectedRoute(EcommerceProductsPage, false),
    },
    { path: '/users/feed', element: withProtectedRoute(UserFeedPage, false) },
    { path: '/users/list', element: withProtectedRoute(UserListPage, false) },
    { path: '/users/profile', element: withProtectedRoute(UserProfilePage) },
    { path: '/users/settings', element: withProtectedRoute(UserSettingsPage) },
  ],
  {
    basename: import.meta.env.BASE_URL,
  }
);
